/**
 * Reliable Telemetry Delivery Queue
 * ---------------------------------
 * Guarantees that an operator's "reason + unlock" write eventually reaches
 * ThingsBoard, even across network drops, WiFi loss, TB restarts, page reloads
 * and (HMI kiosk) power cycles.
 *
 * Why this exists:
 *   The operator screen unlocks a machine by writing `lock_status: "unlocked"`
 *   telemetry that Node-RED listens for. Previously this was a one-shot POST with
 *   no retry — if TB/network was down at that moment the unlock was lost and the
 *   machine stayed locked, blocking cycle-start.
 *
 * Design:
 *   - Every reason+unlock is persisted to localStorage BEFORE any network call.
 *   - A background processor retries (reason -> unlock, in order) with capped
 *     backoff, INDEFINITELY, until the server acknowledges (HTTP 200).
 *   - Writes are idempotent (same reason at same ts / same unlock value), so a
 *     duplicate send after a lost ack is harmless.
 *   - Items are only removed on confirmed delivery.
 *   - Resumes automatically on module load (after reload / reboot) and flushes
 *     the moment connectivity returns (online event / WS reconnect).
 *
 * This module is device-agnostic — the same behaviour is safe on kiosks,
 * tablets and monitors.
 */

import { operatorTelemetry } from "./operatorservice";

const QUEUE_KEY = "reliableTelemetryQueue";
const REQUEST_TIMEOUT = 15000; // ms — fail a hung request fast so it can be retried
const BACKOFF = [1000, 3000, 6000, 10000, 15000]; // ms per attempt, then capped at last value
const SAFETY_INTERVAL = 20000; // ms — periodic re-attempt in case nothing else triggers

// In-memory, per-session callbacks keyed by item id (not persisted — survives only
// within the current page session; after a reload delivery still happens silently
// and the WS "unlocked" event drives the UI instead).
const callbacks = {};
const listeners = new Set();

let processing = false;
let timer = null;
let idSeed = 0;

function loadQueue() {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveQueue(q) {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(q));
  } catch (e) {
    console.error("[reliableTelemetry] failed to persist queue:", e);
  }
  notify();
}

function makeId() {
  // Avoid Date.now()/Math.random() collisions across rapid calls with a seed.
  idSeed += 1;
  return `${idSeed}-${loadQueue().length}-${performance.now().toFixed(0)}`;
}

function getState() {
  const q = loadQueue();
  return {
    pending: q.length,
    online: typeof navigator !== "undefined" ? navigator.onLine : true,
    processing,
    oldestAttempts: q.length ? Math.max(...q.map((x) => x.attempts || 0)) : 0,
  };
}

function notify() {
  const state = getState();
  listeners.forEach((fn) => {
    try {
      fn(state);
    } catch (e) {
      console.error("[reliableTelemetry] listener error:", e);
    }
  });
}

async function processQueue() {
  if (processing) return;
  processing = true;
  notify();
  try {
    const q = loadQueue();
    for (const item of q) {
      try {
        // 1) write the reason, 2) write the unlock — both idempotent.
        await operatorTelemetry("DEVICE", item.deviceId, item.reasonPayload, {
          timeout: REQUEST_TIMEOUT,
        });
        await operatorTelemetry(
          "DEVICE",
          item.deviceId,
          { lock_status: "unlocked" },
          { timeout: REQUEST_TIMEOUT }
        );
        // delivered — remove from the freshest copy of the queue
        const after = loadQueue().filter((x) => x.id !== item.id);
        saveQueue(after);
        const cb = callbacks[item.id];
        if (cb && typeof cb.onDelivered === "function") {
          try {
            cb.onDelivered();
          } catch (e) {
            console.error("[reliableTelemetry] onDelivered error:", e);
          }
        }
        delete callbacks[item.id];
        console.log(
          `[reliableTelemetry] delivered reason+unlock for ${item.deviceId}`
        );
      } catch (e) {
        // bump attempts and keep the item for the next scheduled retry
        const after = loadQueue().map((x) =>
          x.id === item.id ? { ...x, attempts: (x.attempts || 0) + 1 } : x
        );
        saveQueue(after);
        console.warn(
          `[reliableTelemetry] delivery failed for ${item.deviceId}, will retry`,
          e?.message || e
        );
      }
    }
  } finally {
    processing = false;
    notify();
    scheduleNext();
  }
}

function scheduleNext() {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  const q = loadQueue();
  if (!q.length) return;
  const minAttempts = Math.min(...q.map((x) => x.attempts || 0));
  const delay = BACKOFF[Math.min(minAttempts, BACKOFF.length - 1)];
  timer = setTimeout(processQueue, delay);
}

/**
 * Enqueue a reason + unlock for guaranteed delivery.
 * @param {object} args
 * @param {string} args.deviceId
 * @param {object} args.reasonPayload  the { ts, values: { live_reason } } telemetry body
 * @param {function} [args.onDelivered] called once, in this session, when both writes succeed
 * @returns {string} the queued item id
 */
export function enqueueReasonUnlock({ deviceId, reasonPayload, onDelivered }) {
  const id = makeId();
  const item = { id, deviceId, reasonPayload, attempts: 0, createdAt: Date.now() };
  const q = loadQueue();
  q.push(item);
  saveQueue(q);
  if (onDelivered) callbacks[id] = { onDelivered };
  // try immediately
  processQueue();
  return id;
}

/** Trigger an immediate retry of everything queued (call on WS reconnect / manual). */
export function flushReliableQueue() {
  processQueue();
}

/** True if there is still an undelivered reason+unlock for this device. */
export function hasPendingReliableItem(deviceId) {
  return loadQueue().some((x) => x.deviceId === deviceId);
}

/** Subscribe to queue state changes (for a UI banner). Returns an unsubscribe fn. */
export function subscribeReliableQueue(listener) {
  listeners.add(listener);
  try {
    listener(getState());
  } catch {}
  return () => listeners.delete(listener);
}

export function getReliableQueueState() {
  return getState();
}

// --- module init: wire connectivity + safety interval, resume any leftover queue ---
if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    console.log("[reliableTelemetry] online — flushing queue");
    processQueue();
  });
  window.addEventListener("offline", () => notify());
  // periodic safety net (covers cases where no explicit trigger fires)
  setInterval(() => {
    if (loadQueue().length) processQueue();
  }, SAFETY_INTERVAL);
  // resume anything left from a previous session (reload / reboot)
  if (loadQueue().length) {
    console.log("[reliableTelemetry] resuming persisted queue on load");
    scheduleNext();
    processQueue();
  }
}
